import { Autocomplete, TextField, Typography } from "@mui/material";
import { compact } from "lodash-es";
import { forwardRef, useMemo } from "react";

import { useCountriesQuery } from "../../queries/countries";
import { getCountryOptions } from "../../utils/countryUtils";
import { CountryCircleFlag } from "../circle-flag/CountryCircleFlag";

export interface ICountriesSelectProps {
    value: string[];
    label?: string;
    error?: boolean;
    disabled?: boolean;
    disableClearable?: boolean;

    onBlur?(): void;
    onChange?(value: string[]): void;
}

export const CountriesSelect = forwardRef<HTMLInputElement, ICountriesSelectProps>(
    ({ value, label, error, disabled, disableClearable, onBlur, onChange }, ref) => {
        const { data, isLoading } = useCountriesQuery();
        const options = useMemo(() => getCountryOptions(data?.countryByCountryCode), [data]);

        const selectedOptions = compact(value.map((v) => options.find((option) => option.value === v)));

        return (
            <Autocomplete
                multiple={true}
                autoHighlight={true}
                openOnFocus={true}
                size="small"
                options={options}
                value={selectedOptions}
                loading={isLoading}
                disabled={disabled}
                disableClearable={disableClearable}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={ref}
                        error={error}
                        placeholder="Select countries"
                        label={label}
                        inputProps={{
                            ...params.inputProps,
                            autoComplete: "off",
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props}>
                        <CountryCircleFlag countryCode={option.value} />
                        <Typography ml={1}>{option.label}</Typography>
                    </li>
                )}
                onBlur={onBlur}
                onChange={(_event, option) => onChange?.(option.map((o) => o.value))}
            />
        );
    },
);
